/**
 * steps/StorageProviderStep.tsx
 * Storage provider selection step for the onboarding wizard
 *
 * Allows users to choose their data storage location:
 * - Google Drive (recommended) - Cross-device sync
 * - Local Folder - Manual sync to local directory
 * - Skip - Browser-only storage (not portable)
 */

// 1. External dependencies
import React, { useState, useEffect } from 'react';
import { Cloud, FolderOpen, Monitor, AlertTriangle, CheckCircle } from 'lucide-react';

// 2. Internal absolute imports
import { Alert, Card, CardBody, CardFooter, CardHeader, Button, Chip } from '@enfomentis/ui';
import { useSyncConnection } from '@/contexts/useSyncConnection';
import { useAuth } from '@/hooks/useAuth';
import { getSyncErrorMessage } from '@/utils';
import { userSyncSettingsService } from '@/services/UserSyncSettingsService';
import type { ProviderKey } from '@/types/sync';
import type { ConnectionState } from '@/hooks/useProviderConnection';

/**
 * Error state for storage provider connection failures
 * Used to display per-card error messages with retry options
 */
export interface StorageProviderError {
  /** Which provider failed to connect */
  provider: 'google-drive' | 'local';
  /** Human-readable error message */
  message: string;
}

/**
 * Props for the StorageProviderStep component
 *
 * Callback props are optional during transition period (Phase 1-5).
 * Phase 5 will integrate with OnboardingWizard to pass all required callbacks.
 *
 * Note: isConnecting, activeProvider, and error are now derived internally
 * from useSyncConnection() hook state. These props were removed in T3 Phase 1.
 * onChooseFolder was removed in T3 Phase 3 (now handled internally via useSyncConnection).
 */
export interface StorageProviderStepProps {
  /** Callback to advance to next wizard step */
  goToNextStep?: () => void;
  /** Callback when user clicks "Continue without backup" (skip) */
  onSkip?: () => void;
}

/** No-op function for default callback values */
const noop = (): void => {};

/**
 * Maps ConnectionState to StorageProviderError for display.
 * Uses getSyncErrorMessage() for user-facing copy on error states.
 *
 * @param state - Current connection state from useSyncConnection hook
 * @param selectedProvider - The provider that was selected (for error attribution)
 * @returns StorageProviderError or null if no error
 */
function mapConnectionStateToError(
  state: ConnectionState,
  selectedProvider: ProviderKey | null
): StorageProviderError | null {
  // No error states
  if (
    state.status === 'idle' ||
    state.status === 'connecting' ||
    state.status === 'remoteConnecting' ||
    state.status === 'success'
  ) {
    return null;
  }

  // Need a provider to attribute the error to
  const provider = selectedProvider ?? 'google-drive';
  const providerDisplayName = provider === 'google-drive' ? 'Google Drive' : 'Local Directory';

  switch (state.status) {
    case 'offline':
      return {
        provider,
        message: getSyncErrorMessage(
          { code: 'NETWORK_ERROR', message: 'Offline', provider },
          'banner',
          providerDisplayName
        ).description,
      };

    case 'rateLimited': {
      const seconds = Math.ceil(state.retryAfterMs / 1000);
      return {
        provider,
        message: `Too many requests. Please wait ${seconds} seconds before trying again.`,
      };
    }

    case 'needsReauth':
      return {
        provider,
        message: getSyncErrorMessage(
          { code: 'NOT_CONNECTED', message: state.error, provider },
          'banner',
          providerDisplayName
        ).description,
      };

    case 'error':
      return {
        provider,
        message: getSyncErrorMessage(
          { code: 'UNKNOWN_ERROR', message: state.error, provider },
          'banner',
          providerDisplayName
        ).description,
      };

    default:
      // Exhaustive check
      state satisfies never;
      return null;
  }
}

/**
 * Storage provider selection step component
 *
 * Displays three provider options with action buttons.
 * Supports loading states during connection and error display with retry.
 */
export const StorageProviderStep: React.FC<StorageProviderStepProps> = ({
  goToNextStep = noop,
  // onSkip prop kept for backward compatibility but replaced by internal handleSkip
  onSkip: _onSkip = noop,
}) => {
  // ===== HOOK & STATE =====

  // Auth context for userId (needed for skip settings persistence)
  const { user } = useAuth();

  // Global connection state from context
  const { state, connect, disconnect } = useSyncConnection();

  // FSA detection for Local Directory support
  const [hasFileSystemAPI, setHasFileSystemAPI] = useState(true);

  useEffect(() => {
    setHasFileSystemAPI('showDirectoryPicker' in window);
  }, []);

  // Track which provider button was clicked (for loading indicator attribution)
  const [selectedProvider, setSelectedProvider] = useState<ProviderKey | null>(null);

  // Track popup blocked state for Google Drive OAuth
  const [popupBlocked, setPopupBlocked] = useState(false);

  // Track user's explicit choice (for success card display)
  // Only set on successful connection or explicit skip - NOT on errors
  const [userChoice, setUserChoice] = useState<'google-drive' | 'local' | 'skip' | null>(null);

  // Store folder name for Local Directory display
  const [folderName, setFolderName] = useState<string | null>(null);

  // ===== EFFECTS =====

  // Track successful connection to show success card
  // Derives from state.providerKey to persist across remounts (context survives, local state doesn't)
  useEffect(() => {
    if (state.status === 'success') {
      setUserChoice(state.providerKey);
    }
  }, [state]);

  // ===== HANDLERS =====

  /**
   * Handle Google Drive connection button click.
   * Opens OAuth popup synchronously to avoid browser popup blockers,
   * then initiates the connection flow.
   */
  const handleConnectGoogleDrive = async (): Promise<void> => {
    // Open popup synchronously in click handler (before any async work)
    const popup = window.open('about:blank', 'GoogleOAuth', 'width=600,height=700');

    // Handle popup blocked
    if (!popup) {
      setPopupBlocked(true);
      return;
    }

    // Clear popup blocked state on successful open
    setPopupBlocked(false);

    // Track which provider is connecting (for loading indicator)
    setSelectedProvider('google-drive');

    // Initiate OAuth flow with pre-opened popup
    // Hook handles errors internally and updates state; try/catch for defensive coding
    try {
      await connect('google-drive', { oauthPopup: popup });
    } catch (error) {
      // Hook already handles state updates; log for debugging
      console.error('[StorageProviderStep] Google Drive connection error:', error);
    }
  };

  /**
   * Handle Local Folder connection button click.
   * Invokes File System Access API picker (browser-native modal).
   * No popup handling needed - FSA picker doesn't trigger popup blockers.
   */
  const handleChooseFolder = async (): Promise<void> => {
    // Track which provider is connecting (for loading indicator)
    setSelectedProvider('local');

    // Initiate FSA picker flow
    // Hook handles errors internally and updates state; try/catch for defensive coding
    try {
      await connect('local');
      // Note: Folder name could be captured here if connect() returned it,
      // but for now we'll show a generic "Local Folder" in the success card
    } catch (error) {
      // Hook already handles state updates; log for debugging
      console.error('[StorageProviderStep] Local folder connection error:', error);
    }
  };

  /**
   * Handle Skip button click.
   * Persists skip settings to database, then shows the browser storage confirmation card.
   * Settings persistence is non-blocking - if it fails, user still continues.
   */
  const handleSkip = async (): Promise<void> => {
    // Clear any stale connection state (in case user was mid-connection)
    setSelectedProvider(null);
    setPopupBlocked(false);

    // Persist skip settings (non-blocking)
    if (user?.id) {
      try {
        await userSyncSettingsService.updateSettings(user.id, {
          activeSyncProvider: null,
          syncEnabled: false,
          syncOnLogin: false,
        });
      } catch (error) {
        console.error('[StorageProviderStep] Failed to persist skip settings:', error);
        // Continue anyway - don't block onboarding
      }
    }

    setUserChoice('skip');
  };

  /**
   * Handle Change button click on success card.
   * Resets all local state to return to provider selection.
   * Disconnects from current provider if connected.
   */
  const handleChange = async (): Promise<void> => {
    // Reset all local state
    setUserChoice(null);
    setSelectedProvider(null);
    setPopupBlocked(false);
    setFolderName(null);

    // Disconnect from current provider if connected
    if (state.status === 'success') {
      try {
        await disconnect();
      } catch (error) {
        console.error('[StorageProviderStep] Disconnect error:', error);
      }
    }
  };

  // ===== DERIVED STATE =====

  // Loading state - true during connection attempts
  const isConnecting = state.status === 'connecting' || state.status === 'remoteConnecting';

  // Active provider for loading indicator - tracks which button shows spinner
  const activeProvider = selectedProvider;

  // Error state derived from connection state
  const error = mapConnectionStateToError(state, selectedProvider);

  // Show success card when user has made a choice (connected or skipped)
  const showSuccessCard = userChoice !== null;

  // Get connection details for success card display
  const getSuccessCardDetails = (): { title: string; detail: string; icon: React.ReactNode } => {
    switch (userChoice) {
      case 'google-drive':
        return {
          title: 'Connected to Google Drive',
          detail: 'Your journal will sync across all your devices',
          icon: <Cloud className="w-8 h-8 text-primary" />,
        };
      case 'local':
        return {
          title: 'Connected to Local Folder',
          detail: folderName || 'Your journal will be backed up to your selected folder',
          icon: <FolderOpen className="w-8 h-8 text-default-500" />,
        };
      case 'skip':
        return {
          title: 'Using Browser Storage',
          detail: 'Your journal is only stored in this browser. You may lose data if browser storage is cleared.',
          icon: <Monitor className="w-8 h-8 text-default-400" />,
        };
      default:
        return {
          title: '',
          detail: '',
          icon: null,
        };
    }
  };

  const successDetails = getSuccessCardDetails();
  return (
    <Card
      radius="none"
      shadow="none"
      className="h-full max-h-[100dvh] w-full flex flex-col border-0"
    >
      {/* Intro Section */}
      <CardHeader className="flex-col items-start gap-2 pb-4">
        <h2 className="text-lg sm:text-xl font-semibold text-text-primary">
          Your Journal, Your Data, Your Control
        </h2>
        <p className="text-base text-text-secondary">
          Fully portable storage with cross-device sync — no lock‑in.
        </p>
        {!showSuccessCard && (
          <p className="text-base text-text-primary font-medium mt-2">
            Pick where your journal lives:
          </p>
        )}
      </CardHeader>

      {/* Content: Success Card or Provider Selection */}
      <CardBody className="flex-1 overflow-y-auto">
        {showSuccessCard ? (
          /* Success Card - shown after connection or skip */
          <div className="max-w-md mx-auto px-2">
            <Card className="border border-success">
              <CardBody className="p-6">
                <div className="flex flex-col items-center text-center gap-4">
                  {/* Icon with checkmark overlay */}
                  <div className="relative">
                    {successDetails.icon}
                    <CheckCircle className="w-4 h-4 text-success absolute -bottom-1 -right-1 bg-white rounded-full" />
                  </div>

                  {/* Title and detail */}
                  <div>
                    <h3 className="text-lg font-semibold text-text-primary mb-2">
                      {successDetails.title}
                    </h3>
                    <p className={`text-sm ${userChoice === 'skip' ? 'text-warning' : 'text-text-secondary'}`}>
                      {successDetails.detail}
                    </p>
                  </div>
                </div>
              </CardBody>
              <CardFooter className="flex flex-col gap-3 pt-0">
                <Button
                  color="primary"
                  className="w-full min-h-[44px]"
                  onPress={goToNextStep}
                >
                  Continue to next step
                </Button>
                <Button
                  color="default"
                  variant="flat"
                  className="w-full min-h-[44px]"
                  onPress={handleChange}
                >
                  Change
                </Button>
              </CardFooter>
            </Card>
          </div>
        ) : (
          /* Provider Selection Cards */
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl mx-auto px-2">
          {/* Google Drive Card */}
          <Card className="border border-border-light h-full">
            <CardBody className="p-4 flex-1">
              <div className="flex items-start gap-3">
                <Cloud className="w-6 h-6 text-primary mt-1 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="font-medium text-text-primary">Google Drive</span>
                    <Chip size="sm" color="success" variant="flat">Recommended</Chip>
                  </div>
                  <p className="text-sm text-text-secondary">
                    Automatic cross-device synchronization using Google Drive.
                  </p>
                </div>
              </div>
            </CardBody>
            <CardFooter className="flex flex-col items-center gap-2 pt-0">
              {/* Popup blocked error state */}
              {popupBlocked ? (
                <>
                  <Alert color="warning" variant="flat" role="alert" className="w-full">
                    Popup was blocked. Please allow popups for this site and try again.
                  </Alert>
                  <div className="flex flex-wrap justify-center gap-2">
                    <Button
                      color="primary"
                      className="min-h-[44px]"
                      isDisabled={isConnecting}
                      onPress={handleConnectGoogleDrive}
                    >
                      Try Again
                    </Button>
                    <Button
                      color="default"
                      variant="flat"
                      className="min-h-[44px]"
                      isDisabled={isConnecting}
                      onPress={handleSkip}
                    >
                      Continue without backup
                    </Button>
                  </div>
                </>
              ) : error?.provider === 'google-drive' ? (
                /* Connection error state */
                <>
                  <Alert color="danger" variant="flat" role="alert" className="w-full">
                    {error.message}
                  </Alert>
                  <div className="flex flex-wrap justify-center gap-2">
                    <Button
                      color="primary"
                      className="min-h-[44px]"
                      isDisabled={isConnecting}
                      onPress={handleConnectGoogleDrive}
                    >
                      Try Again
                    </Button>
                    <Button
                      color="default"
                      variant="flat"
                      className="min-h-[44px]"
                      isDisabled={isConnecting}
                      onPress={handleSkip}
                    >
                      Continue without backup
                    </Button>
                  </div>
                </>
              ) : (
                /* Normal button state */
                <Button
                  color="primary"
                  className="min-h-[44px]"
                  isLoading={isConnecting && activeProvider === 'google-drive'}
                  isDisabled={isConnecting}
                  onPress={handleConnectGoogleDrive}
                >
                  Connect Google Drive
                </Button>
              )}
            </CardFooter>
          </Card>

          {/* Local Folder Card */}
          <Card className="border border-border-light h-full">
            <CardBody className="p-4 flex-1">
              <div className="flex items-start gap-3">
                <FolderOpen className="w-6 h-6 text-default-500 mt-1 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-medium text-text-primary">Local Folder</span>
                  </div>
                  <p className="text-sm text-text-secondary mb-3">
                    Automatic backup to a local directory.
                    Not recommended for those who need cross-device access.
                  </p>
                  <div className="flex items-center gap-2 text-warning text-sm">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    <span>
                      {hasFileSystemAPI
                        ? 'Not available on mobile devices'
                        : 'Your browser doesn\'t support local folder sync'}
                    </span>
                  </div>
                </div>
              </div>
            </CardBody>
            <CardFooter className="flex flex-col items-center gap-2 pt-0">
              {/* Error state or normal button */}
              {error?.provider === 'local' ? (
                <>
                  <Alert color="danger" variant="flat" role="alert" className="w-full">
                    {error.message}
                  </Alert>
                  <div className="flex flex-wrap justify-center gap-2">
                    <Button
                      color="primary"
                      className="min-h-[44px]"
                      isDisabled={isConnecting}
                      onPress={handleChooseFolder}
                    >
                      Try Again
                    </Button>
                    <Button
                      color="default"
                      variant="flat"
                      className="min-h-[44px]"
                      isDisabled={isConnecting}
                      onPress={handleSkip}
                    >
                      Continue without backup
                    </Button>
                  </div>
                </>
              ) : (
                <Button
                  color="default"
                  className="min-h-[44px]"
                  isLoading={isConnecting && activeProvider === 'local'}
                  isDisabled={!hasFileSystemAPI || isConnecting}
                  onPress={handleChooseFolder}
                >
                  Choose Folder
                </Button>
              )}
            </CardFooter>
          </Card>

          {/* Skip Card */}
          <Card className="border border-border-light h-full">
            <CardBody className="p-4 flex-1">
              <div className="flex items-start gap-3">
                <Monitor className="w-6 h-6 text-default-400 mt-1 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-medium text-text-primary">This Browser</span>
                  </div>
                  <p className="text-sm text-text-secondary mb-3">
                    No portable backup — your journal will be stored only
                    in this browser's local storage.
                  </p>
                  <p className="text-sm text-warning">
                    You may lose your journal if browser data is cleared.
                  </p>
                </div>
              </div>
            </CardBody>
            <CardFooter className="flex justify-center pt-0">
              <Button
                color="default"
                variant="flat"
                className="min-h-[44px]"
                isDisabled={isConnecting}
                onPress={handleSkip}
              >
                Continue without backup
              </Button>
            </CardFooter>
          </Card>
          </div>
        )}
      </CardBody>
    </Card>
  );
};
